import { PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Sector } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
// import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import type { TooltipProps } from 'recharts';

// Função auxiliar para formatar moeda
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

// Interface para os dados do gráfico
interface CategoryChartData {
  name: string;
  value: number;
  color: string;
}

interface CategoryChartProps {
  data: CategoryChartData[];
  total: number;
  title: string;
  viewMoreId?: string;
  onViewMore?: (view: string) => void;
  formatCurrency: (value: number) => string;
}

interface ActiveShapeProps {
  cx: number;
  cy: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
  payload?: unknown;
  percent?: number;
  value?: number;
}

// Renderizador da forma ativa (hover)
const renderActiveShape = (props: unknown) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props as ActiveShapeProps;
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={innerRadius - 6}
        outerRadius={innerRadius - 2}
        fill={fill}
      />
    </g>
  );
};

// Componente interno reutilizável para o gráfico de rosca moderno
export function CategoryDonutChart({ data, total, title, onViewMore, viewMoreId, formatCurrency }: CategoryChartProps) {
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);

  const onPieEnter = (_: unknown, index: number) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(undefined);
  };

  const activeItem = activeIndex !== undefined ? data[activeIndex] : null;
  const displayValue = activeItem ? activeItem.value : total;
  const displayLabel = activeItem ? activeItem.name : "Total";
  const displayColor = activeItem ? activeItem.color : "hsl(var(--foreground))";

  // Ordenar dados por valor para a lista
  // const sortedData = [...data].sort((a, b) => b.value - a.value);

  const hasData = total > 0 && data.length > 0;

  return (
    <Card 
      className={cn(
        "bg-card border hover:border-primary/50 hover:shadow-md transition-all duration-300 shadow-soft h-full flex flex-col",
        viewMoreId && onViewMore && "cursor-pointer"
      )}
      onClick={() => viewMoreId && onViewMore && onViewMore(viewMoreId)}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-h4">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-4 pt-0 min-h-[250px] sm:min-h-[200px]">
        {hasData ? (
          /* Área do Gráfico */
          <div className="relative w-full h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  activeIndex={activeIndex}
                  activeShape={renderActiveShape}
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  onMouseEnter={onPieEnter}
                  onMouseLeave={onPieLeave}
                  stroke="none"
                >
                  {data.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.color}
                      className="transition-all duration-300"
                      strokeWidth={0}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            
            {/* Texto Central */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider max-w-[80px] truncate text-center">
                {displayLabel}
              </span>
              <span 
                className="text-xs sm:text-sm font-semibold tabular-nums transition-colors duration-300"
                style={{ color: activeIndex !== undefined ? displayColor : 'inherit' }}
              >
                {formatCurrency(displayValue)}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex h-[200px] w-full items-center justify-center text-muted-foreground text-sm">
            Sem dados para exibir
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Componente para o gráfico de despesas por categoria
export function ExpenseCategoryChart(props: Omit<CategoryChartProps, 'title' | 'viewMoreId'>) {
  return (
    <CategoryDonutChart 
      {...props} 
      title="Despesas por categoria" 
      viewMoreId="expenses-by-category" 
    />
  );
}

// Componente para o gráfico de receitas por categoria
export function IncomeCategoryChart(props: Omit<CategoryChartProps, 'title' | 'viewMoreId'>) {
  return (
    <CategoryDonutChart 
      {...props} 
      title="Receitas por categoria" 
      viewMoreId="income-by-category" 
    />
  );
}

interface SpendingFrequencyChartProps {
  data: { day: string; amount: number; count: number }[];
  selectedDate?: Date;
  onViewMore?: (view: string) => void;
  formatDate?: (date: Date) => string;
}

// Componente para o gráfico de despesas por mês (frequência)
// Mantido sem alterações estruturais, apenas re-exportado
export function SpendingFrequencyChart({ 
  data, 
  onViewMore,
}: SpendingFrequencyChartProps) {
  const hasData = data.some(item => item.amount > 0);

  return (
    <Card className="bg-card border hover:border-primary/50 hover:shadow-md transition-all duration-300 cursor-pointer shadow-soft"
         onClick={() => onViewMore && onViewMore('spending-frequency')}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-h4">Despesas do mês</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {hasData ? (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis 
                dataKey="day" 
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => {
                  if (typeof v === 'number') return String(v).padStart(2, '0');
                  if (typeof v === 'string') {
                    if (v.includes('/')) {
                      const [day] = v.split('/');
                      return String(day).padStart(2, '0');
                    }
                    if (v.includes('-')) {
                      const parts = v.split('-');
                      const day = parts[2] ?? v;
                      return String(day).padStart(2, '0');
                    }
                    return v;
                  }
                  return String(v);
                }}
              />
              <YAxis 
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => {
                  if (value >= 1000) {
                    return `R$ ${(value / 1000).toFixed(0)}k`;
                  }
                  return `R$ ${value}`;
                }}
                domain={['auto', 'auto']}
                allowDataOverflow={false}
              />
              <Tooltip 
                content={(props) => {
                  const { active, payload, label } = props;
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    const formatDayLabel = (raw: unknown) => {
                      if (typeof raw === 'number') return String(raw).padStart(2, '0');
                      if (typeof raw === 'string') {
                        if (raw.includes('/')) {
                          const [day] = raw.split('/');
                          return String(day).padStart(2, '0');
                        }
                        if (raw.includes('-')) {
                          const parts = raw.split('-');
                          const day = parts[2] ?? raw;
                          return String(day).padStart(2, '0');
                        }
                        return raw;
                      }
                      return String(raw ?? '');
                    };

                    const dayLabel = formatDayLabel(label);

                    return (
                      <div className="bg-card border border-border p-3 rounded-md shadow-md">
                        <p className="text-body-sm font-medium mb-1">Dia {dayLabel}</p>
                        <p className="text-body-sm flex items-center justify-between gap-2">
                          <span>Despesas:</span>
                          <span className="text-amount-sm font-semibold tabular-nums" style={{ color: '#ef4444' }}>
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(data.amount)}
                          </span>
                        </p>
                        {data.count > 0 && (
                          <p className="text-body-sm flex items-center justify-between gap-2 mt-1">
                            <span>Transações:</span>
                            <span className="font-semibold">{data.count}</span>
                          </p>
                        )}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Area 
                type="monotone" 
                dataKey="amount" 
                stroke="#ef4444" 
                fillOpacity={1} 
                fill="url(#colorAmount)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-[200px] w-full items-center justify-center text-muted-foreground text-sm">
            Sem despesas neste mês
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Componente para o gráfico de balanço anual
export const AnnualBalanceChart = ({
  data,
  onViewMore,
}: {
  data: { month: string; balance: number }[];
  onViewMore?: (view: string) => void;
}) => {
  const hasData = data.some(item => item.balance !== 0);

  return (
    <Card className="bg-card border hover:border-primary/50 hover:shadow-md transition-all duration-300 cursor-pointer shadow-soft h-full flex flex-col" onClick={() => onViewMore && onViewMore('annual-balance')}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-h4">Balanço Anual</CardTitle>
      </CardHeader>
      <CardContent className="pt-0 flex-1">
        {hasData ? (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis 
                dataKey="month" 
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                width={60}
                tickFormatter={(value) => {
                  const num = Number(value);
                  const absValue = Math.abs(num);
                  const sign = num < 0 ? '-' : '';
                  if (absValue >= 1000) {
                    return `${sign}R$ ${(absValue / 1000).toFixed(0)}k`;
                  }
                  return `${sign}R$ ${absValue}`;
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="balance" 
                stroke="#3b82f6" 
                fillOpacity={1} 
                fill="url(#colorBalance)"
                strokeWidth={2}
                name="Balanço"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-[200px] w-full items-center justify-center text-muted-foreground text-sm">
            Sem dados de balanço
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Componente para o CustomTooltip
type RechartsPayloadItem = {
  name?: string;
  value?: number | string;
  dataKey?: string | number;
  color?: string;
  stroke?: string;
};

export function CustomTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border p-3 rounded-md shadow-md">
        <p className="text-body-sm font-medium mb-1">{label}</p>
        {payload.map((item: RechartsPayloadItem, index: number) => (
          <p
            key={index}
            className="text-body-sm flex items-center justify-between gap-2"
            style={{ color: item.color || item.stroke }}
          >
            <span>{item.name || String(item.dataKey)}:</span>
            <span className="text-amount-sm font-semibold tabular-nums">
              {typeof item.value === 'number'
                ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.value)
                : String(item.value)}
            </span>
          </p>
        ))}
      </div>
    );
  }

  return null;
}
